import Table, { ColumnsType } from "antd/es/table";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";
import s from "./MobileApplicationsList.module.scss";
import moment from "moment";
import { Button, Tooltip } from "antd";
import { CreditCardOutlined, EditOutlined } from "@ant-design/icons";
import {
  getApplicationDetails,
  getApplicationsList,
  getMessagesList,
  setCurrentPageNumber,
} from "../../redux/ApplicationSlice";
import { getStatusTag } from "../../utils/functions";
import { useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ICreateApplicationResponse } from "../../types";

interface IMobileTable {
  number: number;
  application_number: string;
  common_info: any;
}

const MobileApplicationsList = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const { t } = useTranslation();

  const applicationsList = useSelector(
    (state: RootState) => state.application.applicationsList
  );
  const registeredApplicationsList = useSelector(
    (state: RootState) => state.application.registeredApplicationsList
  );
  const paidApplicationsList = useSelector(
    (state: RootState) => state.application.paidApplicationsList
  );
  const correctedApplicationsList = useSelector(
    (state: RootState) => state.application.correctedApplicationsList
  );
  const priorityApplicationsList = useSelector(
    (state: RootState) => state.application.priorityApplicationsList
  );

  const user = useSelector((state: RootState) => state.user.user);
  const activeTab = useSelector(
    (state: RootState) => state.application.activeTab
  );
  const [pagination, setPagination] = useState({
    current: applicationsList?.pagination?.current_page || 1,
    pageSize: applicationsList?.pagination?.page_size || 30,
    total: applicationsList?.pagination?.total_items || 100,
  });

  const [data, setData] = useState<ICreateApplicationResponse[] | undefined>(
    applicationsList?.results
  );
  const [currentPage, setCurrentPage] = useState<number>(
    applicationsList?.pagination?.current_page || 1
  );

  const [currentRegisteredPage, setCurrentRegisteredPage] = useState<number>(
    registeredApplicationsList?.pagination?.current_page || 1
  );

  const [currentCorrectedPage, setCurrentCorrectedPage] = useState<number>(
    correctedApplicationsList?.pagination?.current_page || 1
  );

  const [currentPaidPage, setCurrentPaidPage] = useState<number>(
    paidApplicationsList?.pagination?.current_page || 1
  );

  const [currentPriorityPage, setCurrentPriorityPage] = useState<number>(
    priorityApplicationsList?.pagination?.current_page || 1
  );

  const currentPageRef = useRef(currentPage);
  const currentRegisteredPageRef = useRef(currentRegisteredPage);
  const currentCorrectedPageRef = useRef(currentCorrectedPage);
  const currentPaidPageRef = useRef(currentPaidPage);
  const currentPriorityPageRef = useRef(currentPriorityPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
    currentRegisteredPageRef.current = currentRegisteredPage;
    currentCorrectedPageRef.current = currentCorrectedPage;
    currentPaidPageRef.current = currentPaidPage;
    currentPriorityPageRef.current = currentPriorityPage;
  }, [
    currentPage,
    currentRegisteredPage,
    currentCorrectedPage,
    currentPaidPage,
    currentPriorityPage,
  ]);

  useEffect(() => {
    const fetchData = async ({ page }: { page: number }) => {
      if (activeTab === "1") {
        const res = await dispatch(getApplicationsList(page));
        if (
          res.payload &&
          res.type === "applications/getApplicationsList/fulfilled"
        ) {
          if ("results" in res.payload) {
            setData(res.payload.results);
            setPagination({
              current: res.payload.pagination?.current_page,
              pageSize: res.payload.pagination?.page_size,
              total: res.payload.pagination?.total_items,
            });
          } else {
            console.error("Unexpected API response:", res.payload);
          }
        }
      }
    };

    fetchData({
      page: currentPageRef.current,
    });

    const interval = setInterval(() => {
      fetchData({
        page: currentPageRef.current,
      });
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch]);

  const getNextPage = async (page: number) => {
    if (activeTab === "1") {
      setCurrentPage(page);
      dispatch(setCurrentPageNumber(page));
      currentPageRef.current = page;
      if (page === undefined || page === null) return;

      try {
        const res = await dispatch(getApplicationsList(page));
        if (!res.payload || !("results" in res.payload)) {
          console.error("Unexpected API response:", res);
          return;
        }
        if (res.type === "applications/getApplicationsList/fulfilled") {
          if ("results" in res.payload) {
            setData(res.payload.results);
            setPagination({
              current: res?.payload?.pagination?.current_page,
              pageSize: res?.payload?.pagination.page_size,
              total: res.payload.pagination.total_items,
            });
          } else {
            console.error("Unexpected API response:", res.payload);
          }
        }
      } catch (error) {
        console.error("Error in getNextPage:", error);
      }
    }
  };

  const columns: ColumnsType<IMobileTable> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("№ заявки"),
      dataIndex: "application_number",
      width: 100,

      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Информация"),
      dataIndex: "common_info",
      width: 200,
      align: "center",
    },
  ];

  const getPaymentAmount = async (id: number) => {
    const res = await dispatch(getApplicationDetails(id));
    if (res.type === "applications/getApplicationDetails/fulfilled") {
      navigate(`/payment/${id}`);
    }
  };

  const getMessages = async (id: number) => {
    const res = await dispatch(getMessagesList(Number(id)));
    if (res.type === "messages/getMessagesList/fulfilled") {
      navigate(`/messages/${id}`);
    }
  };

  const getCurrentPagination = () => {
    switch (activeTab) {
      case "1":
        return pagination;
    }
  };

  return (
    <div>
      <Table
        bordered
        pagination={{ ...getCurrentPagination(), size: "small" }}
        columns={columns}
        className={"mobile_table"}
        onChange={(pagination) => {
          if (pagination?.current) {
            getNextPage(pagination.current);
          }
        }}
        dataSource={data?.map((it, index) => ({
          number: index + 1,
          application_number: it?.application_number,
          status: it?.status,
          common_info: (
            <div>
              <div className={s.table_item}>
                <div className={s.item_name}>{t("Статус")}: </div>
                <div className={s.item_value}>
                  {getStatusTag(it.status, "sm", t)}
                </div>
              </div>
              <div className={s.table_item}>
                <div className={s.item_name}>{t("Дата регистрации")}: </div>
                <div className={s.item_value}>
                  {moment(it?.created_at).format("DD.MM.YYYY")}
                </div>
              </div>

              {it?.status === "new" ||
              it?.status === "incomplete" ||
              it?.status === "corrected" ? null : (
                <div className={s.table_item}>
                  <div className={s.item_name}> {t("Оплата")}: </div>
                  <div className={s.item_value}>
                    <Button
                      className={s.btn}
                      onClick={() => getPaymentAmount(it.id)}
                    >
                      <Tooltip title={t("Просмотреть оплатy")} key={2}>
                        <CreditCardOutlined />
                      </Tooltip>
                    </Button>
                  </div>
                </div>
              )}

              <div className={s.table_item}>
                <div className={s.item_name}> {t("История")}:</div>
                <div className={s.item_value}>
                  <div onClick={() => getMessages(it.id)} className={s.edit}>
                    <Button className={s.btn}>
                      {/* <Tooltip title={t("Просмотреть историю")} key={2}>
                        <MailOutlined className={s.icon} />{" "}
                        {it.user.role === "driver" ||
                          (it.user.role === "regional_manager" &&
                            it?.unread_message_count_owner > 0 && (
                              <div className={s.messages_count}>
                                {it.unread_message_count_owner}
                              </div>
                            ))}
                        {it.user.role === "broker" &&
                          it?.unread_message_count_declarant > 0 && (
                            <div className={s.messages_count}>
                              {it.unread_message_count_declarant}
                            </div>
                          )}
                      </Tooltip> */}
                    </Button>
                  </div>
                </div>
              </div>
              <div className={s.table_item}>
                <div className={s.item_name} style={{ textAlign: "left" }}>
                  {t("Просмотр/Редактирование")}:{" "}
                </div>
                <div className={s.item_value}>
                  <Link
                    to={
                      user?.role === "driver" ||
                      user?.role === "regional_manager"
                        ? `/application/${it?.id}`
                        : `/declarant/application/${it?.id}`
                    }
                    className={s.edit}
                    onClick={() =>
                      it?.id && dispatch(getApplicationDetails(it.id))
                    }
                  >
                    <Button className={s.btn}>
                      <Tooltip title={t("Редактировать заявку")} key={1}>
                        <EditOutlined />
                      </Tooltip>
                    </Button>
                  </Link>
                </div>
              </div>
              {/* {it?.declaration ? (
                <div className={s.table_item}>
                  <div className={s.item_name}> {t("Декларация")}: </div>
                  <div className={s.item_value}>
                    <Link to={it.declaration}>
                      <Button className={s.btn}>
                        <Tooltip title={t("Декларация")} key={2}>
                          <FileDoneOutlined />
                        </Tooltip>
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : null} */}
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default MobileApplicationsList;
